import { Controller } from "@hotwired/stimulus"

// Command-palette search for the help center. The trigger in the rail is a
// fake input; this opens the real one in a <dialog>, fetches the results
// fragment from HelpController#search on every keystroke and drives keyboard
// navigation over the result list.
export default class extends Controller {
  static targets = ["dialog", "input", "results", "result", "hotkey"]
  static values = { url: String, debounce: { type: Number, default: 200 } }

  connect() {
    this.hotkeyListener = this.onHotkey.bind(this)
    document.addEventListener("keydown", this.hotkeyListener)
    this.activeIndex = -1
    this.labelHotkey()
  }

  disconnect() {
    document.removeEventListener("keydown", this.hotkeyListener)
    clearTimeout(this.timeout)
    this.pending?.abort()
  }

  open(event) {
    event?.preventDefault()
    if (this.dialogTarget.open) return

    this.dialogTarget.showModal()
    this.inputTarget.focus()
    this.inputTarget.select()
  }

  close() {
    this.dialogTarget.close()
  }

  closed() {
    clearTimeout(this.timeout)
    this.pending?.abort()
  }

  backdropClick(event) {
    if (event.target === this.dialogTarget) this.close()
  }

  search() {
    clearTimeout(this.timeout)
    this.timeout = setTimeout(() => this.fetchResults(), this.debounceValue)
  }

  navigate(event) {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.moveActive(1)
        break
      case "ArrowUp":
        event.preventDefault()
        this.moveActive(-1)
        break
      case "Enter":
        if (this.activeResult) {
          event.preventDefault()
          this.activeResult.click()
        }
        break
    }
  }

  // ── Private ───────────────────────────────────────────────────────────────

  async fetchResults() {
    // A slow response for "co" must never land after the one for "commentaire".
    this.pending?.abort()
    this.pending = new AbortController()

    const url = `${this.urlValue}?q=${encodeURIComponent(this.inputTarget.value)}`

    try {
      const response = await fetch(url, {
        headers: { Accept: "text/html" },
        signal: this.pending.signal
      })
      if (!response.ok) return

      this.resultsTarget.innerHTML = await response.text()
      this.resultsTarget.scrollTop = 0
      this.activeIndex = -1
    } catch (error) {
      // A superseded request aborts on purpose; anything else leaves the last
      // results on screen rather than breaking the palette.
      if (error.name !== "AbortError") console.error(error)
    }
  }

  moveActive(step) {
    const count = this.resultTargets.length
    if (count === 0) return

    this.activeIndex = (this.activeIndex + step + count) % count
    this.resultTargets.forEach((result, index) => {
      result.classList.toggle("is-active", index === this.activeIndex)
    })
    this.activeResult?.scrollIntoView({ block: "nearest" })
  }

  get activeResult() {
    return this.resultTargets[this.activeIndex]
  }

  labelHotkey() {
    const platform = navigator.userAgentData?.platform ?? navigator.platform ?? ""
    if (platform.toLowerCase().includes("mac")) return

    this.hotkeyTargets.forEach((kbd) => (kbd.textContent = "Ctrl K"))
  }

  onHotkey(event) {
    if (event.key?.toLowerCase() === "k" && (event.metaKey || event.ctrlKey)) {
      event.preventDefault()
      this.open()
    }
  }
}
